/* eslint-disable import/no-named-as-default */
import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import classNames from "classnames";

import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/core/styles";

import MailIcon from "@material-ui/icons/Mail";
import PhoneIcon from "@material-ui/icons/Phone";
import PlaceIcon from "@material-ui/icons/Place";

const cdnPaymentsBaseUrl =
  "https://sportyshop.s3.eu-west-2.amazonaws.com/catalog/common";

const logoUrl =
  "https://s3.eu-west-2.amazonaws.com/sportyshop/catalog/logos/home_logo.png";

const footerColumns = [
  {
    title: "קנייה ושירות",
    key: "service",
    links: [
      {
        key: "shops-footer-link",
        text: "נקודות התקנת צמיגים",
        url: "/our-shops",
      },
      {
        key: "complete-payment",
        text: "השלמת תשלום להזמנת מקדמה",
        url: "/complete-payment",
      },
      {
        key: "returns-and-refund-policy",
        text: "מדיניות החזרות והחזר כספי",
        url: "/returns-and-refund-policy",
      },
      {
        key: "shops-signup",
        text: "הצטרפות כנקודת התקנה",
        url: "/shop/signup",
      },
    ],
  },
  {
    title: "מידע כללי",
    key: "company",
    links: [
      {
        key: "about-link",
        text: "אודות ספורטישופ",
        url: "/about-us",
      },
      {
        key: "imprint",
        text: "פרטים משפטיים",
        url: "/imprint",
      },
      {
        key: "tou",
        text: "תנאי השימוש בשירות",
        url: "/terms-of-use",
      },
      {
        key: "privacy-policy",
        text: "הצהרת פרטיות",
        url: "/privacy-policy",
      },
    ],
  },
  {
    title: "אמון ובטיחות",
    key: "trust",
    links: [
      {
        key: "accessibility",
        text: "הצהרת נגישות",
        url: "/accessibility-policy",
      },
      {
        key: "gdpr-statement",
        text: "הצהרת הגנת מידע (GDPR)",
        url: "/data-protection-policy-and-gdpr",
      },
      {
        key: "facebook",
        text: "עקבו אחרינו בפייסבוק",
        url: "https://www.facebook.com/sportyshop.moto",
        external: true,
      },
    ],
  },
];

const paymentMethods = [
  {
    key: "mastercard",
    alt: "MasterCard",
    src: `${cdnPaymentsBaseUrl}/mastercard.png`,
  },
  {
    key: "visa",
    alt: "Visa",
    src: `${cdnPaymentsBaseUrl}/visa.png`,
  },
  {
    key: "paypal",
    alt: "PayPal",
    src: `${cdnPaymentsBaseUrl}/paypal.png`,
  },
];

const highlights = ["מהיבואן לצרכן", "תור בקליק", "מוסכים מקצועיים"];

const styles = (theme) => ({
  footer: {
    marginTop: theme.spacing(7),
    background: "linear-gradient(180deg, #ffffff 0%, #f7f8fb 100%)",
    borderTop: "1px solid #e6e9ef",
    color: "#1f2933",
  },
  inner: {
    width: "100%",
    maxWidth: 1180,
    marginLeft: "auto",
    marginRight: "auto",
    padding: `${theme.spacing(4)}px ${theme.spacing(2)}px ${theme.spacing(
      3
    )}px`,
  },
  topSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  brandCard: {
    border: "1px solid #e6e9ef",
    borderRadius: 24,
    backgroundColor: "#ffffff",
    boxShadow: "0 18px 45px rgba(17, 24, 39, 0.06)",
    padding: theme.spacing(3),
  },
  logoLink: {
    display: "inline-flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  logoImg: {
    width: 162,
    maxWidth: "100%",
  },
  brandText: {
    color: "#52606d",
    fontSize: 15,
    lineHeight: 1.8,
    maxWidth: 520,
  },
  highlightRow: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(-0.5),
    marginLeft: theme.spacing(-0.5),
  },
  highlight: {
    margin: theme.spacing(0.5),
    padding: `${theme.spacing(0.75)}px ${theme.spacing(1.5)}px`,
    borderRadius: 999,
    backgroundColor: "#f1f5f9",
    color: "#263445",
    fontSize: 13,
    fontWeight: 600,
  },
  contactPanel: {
    borderRadius: 24,
    backgroundColor: "#111827",
    color: "#ffffff",
    padding: theme.spacing(3),
  },
  contactTitle: {
    color: "#ffffff",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  contactSubtitle: {
    color: "rgba(255, 255, 255, 0.72)",
    fontSize: 14,
    lineHeight: 1.7,
    marginBottom: theme.spacing(2),
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minHeight: 42,
    color: "#ffffff",
    textDecoration: "none",
    fontSize: 15,
  },
  contactIcon: {
    color: "#f6c34f",
    fontSize: 20,
  },
  linksGrid: {
    borderTop: "1px solid #e6e9ef",
    paddingTop: theme.spacing(3),
  },
  sectionTitle: {
    color: "#111827",
    fontSize: 16,
    fontWeight: 800,
    marginBottom: theme.spacing(1.25),
  },
  typoLink: {
    color: "#52606d",
    display: "block",
    fontSize: 14,
    lineHeight: 1.9,
    textDecoration: "none",
    "&:hover": {
      color: "#111827",
      textDecoration: "underline",
    },
  },
  paymentsTitle: {
    color: "#111827",
    fontSize: 16,
    fontWeight: 800,
    marginBottom: theme.spacing(1.25),
  },
  payments: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    margin: theme.spacing(-0.5),
  },
  paymentBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 74,
    height: 44,
    margin: theme.spacing(0.5),
    padding: `${theme.spacing(0.75)}px ${theme.spacing(1.25)}px`,
    borderRadius: 14,
    backgroundColor: "#ffffff",
    border: "1px solid #e6e9ef",
  },
  paymentMethodLogo: {
    maxHeight: 27,
    maxWidth: 64,
  },
  businessDetails: {
    color: "#697586",
    fontSize: 13,
    lineHeight: 1.8,
    marginTop: theme.spacing(1),
  },
  bottomBar: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    borderTop: "1px solid #e6e9ef",
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2.5),
  },
  bottomLinks: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  bottomLink: {
    color: "#52606d",
    fontSize: 13,
    textDecoration: "none",
    "&:hover": {
      color: "#111827",
      textDecoration: "underline",
    },
  },
  "@media (min-width: 720px)": {
    inner: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
    },
    topSection: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.6fr) minmax(280px, 0.8fr)",
      alignItems: "stretch",
    },
    bottomBar: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  "@media (min-width: 1024px)": {
    footer: {
      marginTop: theme.spacing(9),
    },
    inner: {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(3),
    },
    brandCard: {
      padding: theme.spacing(4),
    },
    contactPanel: {
      padding: theme.spacing(4),
    },
  },
});

function renderFooterLink(item, classes) {
  if (item.external) {
    return (
      <Typography
        className={classes.typoLink}
        component="a"
        href={item.url}
        key={item.key}
        rel="noopener noreferrer"
        target="_blank"
      >
        {item.text}
      </Typography>
    );
  }

  return (
    <Typography
      className={classes.typoLink}
      component={Link}
      key={item.key}
      to={item.url}
      variant="body2"
    >
      {item.text}
    </Typography>
  );
}

function CatalogFooter({ classes }) {
  return (
    <footer className={classNames(classes.footer)}>
      <div className={classes.inner}>
        <div className={classes.topSection}>
          <div className={classes.brandCard}>
            <a className={classes.logoLink} href="/">
              <img
                alt="SportyShop"
                className={classes.logoImg}
                src={logoUrl}
              />
            </a>
            <Typography className={classes.brandText} component="p">
              צמיגים לאופנועים מהיבואן לצרכן, עם מלאי ענק, מחירים נוחים
              וקביעת התקנה בקליק במוסכים מקצועיים ברחבי הארץ.
            </Typography>
            <div className={classes.highlightRow}>
              {highlights.map((item) => (
                <Typography
                  className={classes.highlight}
                  component="span"
                  key={item}
                  variant="body2"
                >
                  {item}
                </Typography>
              ))}
            </div>
          </div>

          <div className={classes.contactPanel}>
            <Typography className={classes.contactTitle} variant="h6">
              צריכים עזרה בבחירת צמיג?
            </Typography>
            <Typography className={classes.contactSubtitle} component="p">
              דברו איתנו ונכוון אתכם למידה, למותג ולנקודת ההתקנה המתאימים.
            </Typography>
            <Typography
              className={classes.contactItem}
              component="a"
              href="tel:0549200911"
              variant="body2"
            >
              <PhoneIcon className={classes.contactIcon} />
              איתמר: 054-9200911
            </Typography>
            <Typography
              className={classes.contactItem}
              component="a"
              href="tel:0584453700"
              variant="body2"
            >
              <PhoneIcon className={classes.contactIcon} />
              מארק: 058-4453700
            </Typography>
            <Typography
              className={classes.contactItem}
              component="a"
              href="mailto:service@sportyshop.co.il"
              variant="body2"
            >
              <MailIcon className={classes.contactIcon} />
              service@sportyshop.co.il
            </Typography>
            <Typography
              className={classes.contactItem}
              component="div"
              variant="body2"
            >
              <PlaceIcon className={classes.contactIcon} />
              התקנות בפריסה ארצית
            </Typography>
          </div>
        </div>

        <Grid className={classes.linksGrid} container spacing={4}>
          {footerColumns.map((footer) => (
            <Grid item key={footer.key} sm={6} xs={12} md={3}>
              <Typography className={classes.sectionTitle} variant="h6">
                {footer.title}
              </Typography>
              {footer.links.map((item) => renderFooterLink(item, classes))}
            </Grid>
          ))}

          <Grid item sm={6} xs={12} md={3}>
            <Typography className={classes.paymentsTitle} variant="h6">
              תשלום מאובטח
            </Typography>
            <div className={classes.payments}>
              {paymentMethods.map((payment) => (
                <span className={classes.paymentBadge} key={payment.key}>
                  <img
                    alt={payment.alt}
                    className={classes.paymentMethodLogo}
                    src={payment.src}
                  />
                </span>
              ))}
            </div>
            <Typography className={classes.businessDetails} component="p">
              פקס: 077-3181220
              <br />
              מספר עוסק: 03696453-4
            </Typography>
          </Grid>
        </Grid>

        <div className={classes.bottomBar}>
          <div className={classes.bottomLinks}>
            <Typography
              className={classes.bottomLink}
              component={Link}
              to="/privacy-policy"
              variant="body2"
            >
              פרטיות
            </Typography>
            <Typography
              className={classes.bottomLink}
              component={Link}
              to="/terms-of-use"
              variant="body2"
            >
              תנאי שימוש
            </Typography>
            <Typography
              className={classes.bottomLink}
              component={Link}
              to="/accessibility-policy"
              variant="body2"
            >
              נגישות
            </Typography>
          </div>
        </div>
      </div>
    </footer>
  );
}

CatalogFooter.propTypes = {
  classes: PropTypes.object,
};

export default withStyles(styles)(CatalogFooter);
